import * as path from "path";

import { pathExists } from "fs-extra";
import mime from "mime-types";

import { engineFactory } from "../../../BuildTasks/Engine/AcquireFormEngine";
import { EngineVersionUtils } from "../../../BuildTasks/EngineVersionUtils";
import { httpGet } from "../../../Commons/HttpServer/AttributeRouting/Methods";
import { NotFoundWebError } from "../../../Commons/HttpServer/AttributeRouting/NotFoundWebError";
import { httpType, httpUrlParam } from "../../../Commons/HttpServer/AttributeRouting/Params";
import { httpReturn } from "../../../Commons/HttpServer/AttributeRouting/Results";
import {
    FileStreamResult,
    StreamReturnResult,
} from "../../../Commons/HttpServer/AttributeRouting/Results/StreamResultHandler";
import { SingletonController } from "../../../Commons/HttpServer/ExpressWrapper/SingletonController";

export class BuildServerPublishedEngineController extends SingletonController {
    private readonly candyModulesDirectory: string;
    private readonly usedEngines: string[] = [];

    public constructor(candyModulesDirectory: string) {
        super();
        this.candyModulesDirectory = candyModulesDirectory;
    }

    @httpGet(
        "/public/scripts/engineLoader_v:engineName.js",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("engineName", httpType.String)
    )
    public async getEngineLoaderScript(engineName: string): Promise<StreamReturnResult> {
        const fileName = `engineLoader_v${engineName}.js`;
        return this.fetchEngineLoaderScript(fileName);
    }

    @httpGet(
        "/public/scripts/candy_v:engineName.js",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("engineName", httpType.String)
    )
    public async getEngineScript(engineName: string): Promise<StreamReturnResult> {
        const fileName = `candy_v${engineName}.js`;
        return this.fetchEngineScript(fileName);
    }

    @httpGet(
        "/public/scripts/:filename.js",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("filename", httpType.String)
    )
    public async getEngineDependencyScript(fileName: string): Promise<StreamReturnResult> {
        return this.findResourceInUsedEngines(`scripts/${fileName}.js`, "application/javascript");
    }

    @httpGet(
        "/public/scripts/:chunkname.candy_v:engineName.js",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("chunkname", httpType.String),
        httpUrlParam("engineName", httpType.String)
    )
    public async getEngineChunk(chunkname: string, engineName: string): Promise<StreamReturnResult> {
        const fileName = `${chunkname}.candy_v${engineName}.js`;
        return this.fetchEngineScript(fileName);
    }

    @httpGet(
        "/public/styles/candy_v:engineName.css",
        httpReturn.StreamWithMime("text/css"),
        httpUrlParam("engineName", httpType.String)
    )
    public async getEngineStyles(engineName: string): Promise<StreamReturnResult> {
        const fileName = `candy_v${engineName}.css`;
        return this.fetchEngineStyleSheet(fileName);
    }

    @httpGet(
        "/public/scripts/candy_v:engineName.js.map",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("engineName", httpType.String)
    )
    public async getEngineScriptSourceMap(engineName: string): Promise<StreamReturnResult> {
        const fileName = `candy_v${engineName}.js.map`;
        return this.fetchEngineScript(fileName);
    }

    @httpGet(
        "/public/scripts/:chunkname.candy_v:engineName.js.map",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("chunkname", httpType.String),
        httpUrlParam("engineName", httpType.String)
    )
    public async getEngineChunkSourceMap(chunkname: string, engineName: string): Promise<StreamReturnResult> {
        const fileName = `${chunkname}.candy_v${engineName}.js.map`;
        return this.fetchEngineScript(fileName);
    }

    @httpGet(
        "/public/styles/candy_v:engineName.css.map",
        httpReturn.StreamWithMime("text/css"),
        httpUrlParam("engineName", httpType.String)
    )
    public async getEngineStylesSourceMap(engineName: string): Promise<StreamReturnResult> {
        const fileName = `candy_v${engineName}.css.map`;
        return this.fetchEngineStyleSheet(fileName);
    }

    @httpGet("/public/fonts/:filename", httpReturn.Stream, httpUrlParam("filename", httpType.String))
    public async getFormFont(fileName: string): Promise<StreamReturnResult> {
        return this.findResourceInUsedEngines(`fonts/${fileName}`, mime.lookup(fileName));
    }

    @httpGet("/public/images/:filename", httpReturn.Stream, httpUrlParam("filename", httpType.String))
    public async getFormImages(fileName: string): Promise<StreamReturnResult> {
        return this.findResourceInUsedEngines(`images/${fileName}`, mime.lookup(fileName));
    }

    private async findResourceInUsedEngines(
        resourcePath: string,
        mimeType?: string | false
    ): Promise<StreamReturnResult> {
        for (const pathToEngine of this.usedEngines) {
            const fileNamePath = path.join(pathToEngine, resourcePath);
            if (await pathExists(fileNamePath)) {
                return new FileStreamResult(fileNamePath, mimeType);
            }
        }
        throw new NotFoundWebError("File not found", true);
    }

    private async fetchEngineLoaderScript(name: string): Promise<FileStreamResult> {
        const engineVersion = EngineVersionUtils.extractVersionFromEngineLoaderFileName(name);
        return this.getScript(engineVersion, name);
    }

    private async fetchEngineScript(name: string): Promise<FileStreamResult> {
        const engineVersion = EngineVersionUtils.extractVersionFromEngineFileName(name);
        return this.getScript(engineVersion, name);
    }

    private async fetchEngineStyleSheet(name: string): Promise<FileStreamResult> {
        const engineVersion = EngineVersionUtils.extractVersionFromEngineFileName(name);
        return this.getStyleSheet(engineVersion, name);
    }

    private async getScript(engineVersion: string, name: string): Promise<FileStreamResult> {
        return this.getPublishedResource(engineVersion, `scripts/${name}`);
    }

    private async getStyleSheet(engineVersion: string, name: string): Promise<FileStreamResult> {
        return this.getPublishedResource(engineVersion, `styles/${name}`);
    }

    private async getPublishedResource(engineVersion: string, resourcePath: string): Promise<FileStreamResult> {
        const pathToPublishedEngine = await this.getPathToPublishedEngine(engineVersion);
        return new FileStreamResult(`${pathToPublishedEngine}/${resourcePath}`);
    }

    private async getPathToPublishedEngine(engineVersion: string): Promise<string> {
        const engine = engineFactory(
            { type: "PublishedEngine", version: engineVersion },
            this.candyModulesDirectory,
            false
        );
        const result = await engine.currentValue;
        if (!this.usedEngines.includes(result)) {
            this.usedEngines.push(result);
        }
        return result;
    }
}
